import type { Transaction, TransactionCategory } from "@/types/portfolio.types";

export const CATEGORY_LABELS: Record<TransactionCategory, string> = {
  royalties: "Royalties",
  advance: "Advances",
  sync_fee: "Sync Fees",
  brand_fee: "Brand Fees",
  production: "Production",
  studio: "Studio",
  legal: "Legal",
  marketing: "Marketing",
  talent: "Talent",
  investment: "Investment",
  distribution: "Distribution",
  other: "Other",
};

export type BalanceResult = {
  balance: number;
  completedIn: number;
  completedOut: number;
  pendingIn: number;
  pendingOut: number;
};

export function computeBalance(txns: Transaction[]): BalanceResult {
  let completedIn = 0;
  let completedOut = 0;
  let pendingIn = 0;
  let pendingOut = 0;

  for (const tx of txns) {
    if (tx.status === "completed") {
      if (tx.direction === "in") completedIn += tx.amount;
      else completedOut += tx.amount;
    } else {
      if (tx.direction === "in") pendingIn += tx.amount;
      else pendingOut += tx.amount;
    }
  }

  return {
    balance: completedIn - completedOut,
    completedIn,
    completedOut,
    pendingIn,
    pendingOut,
  };
}

export type MonthlyBucket = {
  month: string;
  label: string;
  inflow: number;
  outflow: number;
  net: number;
  cumulative: number;
  isProjected: boolean;
};

const MONTH_NAMES = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

export function buildMonthlyBuckets(txns: Transaction[], todayIso: string): MonthlyBucket[] {
  const currentMonth = todayIso.slice(0, 7);

  const months: string[] = [];
  for (let y = 2025, m = 10; months.length < 8; m++) {
    if (m > 12) {
      m = 1;
      y++;
    }
    months.push(`${y}-${String(m).padStart(2, "0")}`);
  }

  const inflowMap: Record<string, number> = {};
  const outflowMap: Record<string, number> = {};
  for (const tx of txns) {
    const key = tx.isoDate.slice(0, 7);
    if (tx.direction === "in") {
      inflowMap[key] = (inflowMap[key] ?? 0) + tx.amount;
    } else {
      outflowMap[key] = (outflowMap[key] ?? 0) + tx.amount;
    }
  }

  let cumulative = 0;
  return months.map((month) => {
    const inflow = inflowMap[month] ?? 0;
    const outflow = outflowMap[month] ?? 0;
    const net = inflow - outflow;
    cumulative += net;
    return {
      month,
      label: MONTH_NAMES[parseInt(month.slice(5, 7), 10) - 1],
      inflow,
      outflow,
      net,
      cumulative,
      isProjected: month > currentMonth,
    };
  });
}

export type CategoryGroup = {
  category: TransactionCategory;
  label: string;
  amount: number;
  pct: number;
};

export function groupByCategory(txns: Transaction[], direction: "in" | "out"): CategoryGroup[] {
  const filtered = txns.filter((t) => t.direction === direction && t.category);
  const total = filtered.reduce((s, t) => s + t.amount, 0);
  const map: Record<string, number> = {};

  for (const tx of filtered) {
    const cat = tx.category;
    if (!cat) continue;
    map[cat] = (map[cat] ?? 0) + tx.amount;
  }

  return Object.entries(map)
    .map(([cat, amount]) => ({
      category: cat as TransactionCategory,
      label: CATEGORY_LABELS[cat as TransactionCategory] ?? cat,
      amount,
      pct: total > 0 ? Math.round((amount / total) * 100) : 0,
    }))
    .sort((a, b) => b.amount - a.amount);
}

export function computePeriodChange(txns: Transaction[], todayIso: string): number {
  const currentMonth = todayIso.slice(0, 7);
  const d = new Date(todayIso);
  d.setMonth(d.getMonth() - 1);
  const prevMonth = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;

  let currentNet = 0;
  let prevNet = 0;

  for (const tx of txns) {
    const m = tx.isoDate.slice(0, 7);
    const val = tx.direction === "in" ? tx.amount : -tx.amount;
    if (m === currentMonth) currentNet += val;
    else if (m === prevMonth) prevNet += val;
  }

  if (prevNet === 0) return currentNet > 0 ? 100 : 0;
  return Math.round(((currentNet - prevNet) / Math.abs(prevNet)) * 100);
}
